import { __assign, __read } from "tslib";
import { useCapabilities } from '@office-start/capabilities-service';
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { logDebugCacheEvent } from '@office-start/instrumentation-service';
import { IDLE_LOW_PRIORITY, scheduleWork, } from '@office-start/scheduler-service';
import { useShellShim } from '@office-start/shell-shim-service';
import { logError } from '@office-start/telemetry-service';
import { useEffect, useState } from 'react';
import { AppsShimDataSourceAppCollectors, AppsShimDataSourceShellDataComplete, } from '../models/Constants';
import { parseWorkload } from '../utilities/parseWorkload';
export var useThirdPartyWorkloads = function (enabled) {
    if (enabled === void 0) { enabled = true; }
    var officeComponentContext = useOfficeComponentContext().data;
    var _a = __read(useState(), 2), workloads = _a[0], setWorkloads = _a[1];
    var _b = __read(useState(false), 2), isSuccess = _b[0], setIsSuccess = _b[1];
    var _c = __read(useState(false), 2), isError = _c[0], setIsError = _c[1];
    var _d = __read(useState(false), 2), isComplete = _d[0], setIsComplete = _d[1];
    var shellShim = useShellShim();
    var capabilities = useCapabilities().data;
    var _e = __read(useState(false), 2), callbackRegistered = _e[0], setCallBackRegistered = _e[1];
    useEffect(function () {
        var _a;
        //this is the subscription logic that should only run once when all dependencies are ready for use
        if (enabled &&
            (shellShim === null || shellShim === void 0 ? void 0 : shellShim.shellShim) &&
            capabilities &&
            !callbackRegistered) {
            //prevent this logic from rerunning due to update in workload dependency
            setCallBackRegistered(true);
            (_a = shellShim === null || shellShim === void 0 ? void 0 : shellShim.shellShim) === null || _a === void 0 ? void 0 : _a.Apps.OnAppsUpdated(function (evt) {
                var _a;
                var iconFetchesStarted = 0;
                var iconFetchesCompleted = 0;
                var appFetchComplete = false;
                appFetchComplete =
                    evt.DataSource === AppsShimDataSourceAppCollectors ||
                        evt.DataSource === AppsShimDataSourceShellDataComplete;
                if (evt && evt.Apps) {
                    setIsSuccess(true);
                    setIsError(false);
                    var nonFirstParty = evt.Apps.filter(function (app) {
                        return app.CollectorId !== 'FirstParty' &&
                            !(workloads === null || workloads === void 0 ? void 0 : workloads.find(function (workload) { return workload.Id === app.Id; }));
                    });
                    //do not filter dynamics apps if showBusinessApps is enabled
                    if (!(capabilities === null || capabilities === void 0 ? void 0 : capabilities.showBusinessApps)) {
                        nonFirstParty = nonFirstParty.filter(function (app) { return app.CollectorId !== 'Dynamics'; });
                    }
                    // eslint-disable-next-line prefer-const
                    var parsedWorkloads_1 = [];
                    // eslint-disable-next-line prefer-const
                    var appsWithoutIconAnonUrl_1 = [];
                    nonFirstParty.forEach(function (app) {
                        var parsedWorkload = parseWorkload(app);
                        parsedWorkloads_1.push(parsedWorkload);
                        if (!app.IconAnonymousUrl) {
                            appsWithoutIconAnonUrl_1.push(app);
                        }
                    });
                    appsWithoutIconAnonUrl_1.forEach(function (app) {
                        iconFetchesStarted++;
                        scheduleWork(function () {
                            var _a;
                            (_a = shellShim === null || shellShim === void 0 ? void 0 : shellShim.shellShim) === null || _a === void 0 ? void 0 : _a.Apps.GetAuthenticatedAppIcon(app, function (appIcon) {
                                iconFetchesCompleted++;
                                if (appIcon) {
                                    var workloadIndex = parsedWorkloads_1.findIndex(function (thirdPartyWorkload) {
                                        return thirdPartyWorkload.Id.toLowerCase() ===
                                            app.Id.toLowerCase();
                                    });
                                    parsedWorkloads_1[workloadIndex] = __assign(__assign({}, parsedWorkloads_1[workloadIndex]), {
                                        ThirdPartyIcon: "data:".concat(appIcon.MimeType, ";base64,").concat(appIcon.Base64EncodedImage),
                                    });
                                }
                                if (appFetchComplete &&
                                    iconFetchesStarted ===
                                        iconFetchesCompleted &&
                                    officeComponentContext) {
                                    // Commented out until we get further instructions from CELA on guid appId in CosmosDB
                                    // invalidateUsedThirdPartyAppCache(
                                    //     parsedWorkloads,
                                    //     officeComponentContext,
                                    // );
                                    setWorkloads(parsedWorkloads_1);
                                }
                            });
                        }, IDLE_LOW_PRIORITY);
                    });
                    // Commented out until we get further instructions from CELA on guid appId in CosmosDB
                    // if (
                    //     appFetchComplete &&
                    //     iconFetchesStarted === iconFetchesCompleted &&
                    //     officeComponentContext
                    // ) {
                    //     invalidateUsedThirdPartyAppCache(
                    //         parsedWorkloads,
                    //         officeComponentContext,
                    //     );
                    // }
                    setWorkloads(parsedWorkloads_1);
                }
                else {
                    setIsError(true);
                    setIsSuccess(false);
                    if (evt && evt.FailureReason) {
                        var debugInfo = "useThirdPartyWorkloads fail in shellShim call. FailureReason: ".concat(evt.FailureReason);
                        var eventName = 'Shell_Shim_Internal_Error';
                        logError({
                            eventName: eventName,
                            debugInfo: debugInfo,
                            errorType: 'Shell_Apps_Update_Error',
                        });
                        // TODO: Remove when Otel is enabled
                        logDebugCacheEvent(eventName, (_a = {}, _a["DebugInfo" /* InstrumentationParam.DebugInfo */] = debugInfo, _a), []);
                    }
                }
                if (evt) {
                    setIsComplete(evt.DataSource !== 5 /* AppDataSource.ShellDataIncomplete */);
                }
            });
        }
        else if (shellShim === null || shellShim === void 0 ? void 0 : shellShim.error) {
            setIsError(true);
            setIsComplete(true);
        }
    }, [
        enabled,
        callbackRegistered,
        setCallBackRegistered,
        capabilities,
        shellShim,
        workloads,
        officeComponentContext,
    ]);
    return {
        data: workloads,
        isSuccess: isSuccess,
        isError: isError,
        isComplete: isComplete,
    };
};
//# sourceMappingURL=useThirdPartyWorkloads.js.map